import { cva, type VariantProps } from 'class-variance-authority';

import { BADGE_SIZES, type BadgeSize } from '@/hen-ui/constants/badgeTheme';

export { BADGE_SIZES, type BadgeSize };

export const BADGE_WEIGHTS = ['regular', 'medium', 'semibold', 'bold'] as const;
export type BadgeWeight = (typeof BADGE_WEIGHTS)[number];

// Literal class strings, so Tailwind's scanner finds every size class.
const badgeSizeClass = {
  sm: 'badge-size-sm',
  lg: 'badge-size-lg',
  xl: 'badge-size-xl',
} as const satisfies Record<BadgeSize, string>;

const badgeSizeMdClass = {
  sm: 'md:badge-size-sm',
  lg: 'md:badge-size-lg',
  xl: 'md:badge-size-xl',
} as const satisfies Record<BadgeSize, string>;

const badgeSizeLgClass = {
  sm: 'lg:badge-size-sm',
  lg: 'lg:badge-size-lg',
  xl: 'lg:badge-size-xl',
} as const satisfies Record<BadgeSize, string>;

const badgeWeightClass = {
  regular: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
} as const satisfies Record<BadgeWeight, string>;

/**
 * Badge variant styles using Class Variance Authority (CVA)
 *
 * Defines visual variants and size options for the Badge component.
 * All styles use design tokens from the HEN Design System.
 *
 * These styles override the shadcn badge base styles. The `!important` flags
 * are necessary to override shadcn's default styles (rounded-full, text-xs, etc.)
 * with HEN Design System tokens.
 *
 * ## Variants
 * - **primary** (default): Dark purple background (bg-primary-900) with white text - Most prominent, use for primary emphasis
 * - **secondary**: Light gray background (bg-neutral-100) with secondary text (text-secondary-700) - Use for secondary emphasis or less prominent counts
 * - **secondaryFill**: White text on secondary brand (bg-secondary-700) - Use for badges on neutral/dark strips (e.g. degree card superlative)
 * - **light**: Light gray background (bg-neutral-100) with purple text (text-primary-700) - Subtle emphasis, use for secondary labels
 * - **dark**: Dark neutral background (bg-neutral-900) with white text - High contrast option on light backgrounds where primary color is not appropriate
 * - **accent**: Accent background (bg-accent-200) with inherited text color - Use sparingly to draw attention to special states or highlighted metadata
 * - **outline**: White background with border (border-neutral-200) - Use on colored backgrounds for visibility
 * - **neutral**: Minimal gray background (bg-neutral-50) - Least prominent, for tags/metadata
 * - **tag**: Neutral background with secondary-700 text - For degree/topic tags
 * - **surfaceAccent**: Theme v2 accent surface with dark text
 *
 * ## Tones
 * Optional neutral, success, warning, error, brand, inverse, and ghost tones
 * override the legacy `variant` colors. Each tone pairs its background and
 * text through Badge-scoped tokens. `subtle` is the default emphasis.
 * `strong` is a deeper tint of the same tone and stays light, except inverse,
 * which stays a dark fill.
 *
 * ## Sizes
 * - **sm** (default): Compact for inline use. Radius, weight, type size,
 *   padding, and icon size read theme tokens and fall back to the current badge.
 * - **lg**: Larger for standalone badges, l1 type with xs horizontal padding
 * - **xl**: Body b5 type with 4px by 12px padding, 30px tall, and a 13px icon
 *
 * Each size class sets the Badge-local size variables (`badge-size-{size}`,
 * see `constants/badgeTheme.ts`), and each size reads its own
 * `--cds-badge-{size}-*` tokens. `sm` and `lg` also read the shared badge
 * tokens they read before, so their defaults stay as they were.
 *
 * `size-md` and `size-lg` switch the whole size from the tablet or desktop
 * breakpoint up. `weight` replaces the theme weight for one badge.
 *
 * ## Base Styles (Override shadcn defaults)
 * - Layout: flex with max-w-fit and gap-1 for icon spacing
 * - Border radius: theme token, falling back to rounded-xl (overrides shadcn's rounded-full)
 * - Typography: font-body and the theme weight token, falling back to 700 (overrides shadcn's font-semibold)
 * - Focus: Custom shadow for HEN design system (overrides shadcn's ring)
 * - Dark mode: Disabled (HEN doesn't use dark mode)
 *
 * ## Design Token Overrides
 * - Spacing: size-scoped padding (overrides shadcn's px-2.5 py-0.5)
 * - Typography: size-scoped type size (overrides shadcn's text-xs)
 * - Border: Variant-specific borders (overrides shadcn's default border)
 */
export const badgeVariants = cva(
  [
    'flex max-w-fit gap-1 rounded-badge font-badge px-badge-size-x py-badge-size-y text-badge-size min-h-badge-size !ring-0 !ring-offset-0 not-italic font-body focus:!outline-none focus:!shadow-focus-shadow',
    '[&_svg]:!size-badge-size-icon [&_[data-hen-icon]]:!size-badge-size-icon',
    'dark:bg-unset dark:text-unset dark:border-unset text-unset border-unset border-unset',
  ],
  {
    variants: {
      variant: {
        primary: 'text-white border-0 border-none bg-primary-900',
        secondary: 'text-secondary-700 border-0 border-none bg-neutral-100',
        secondaryFill: 'text-white border-0 border-none bg-secondary-700',
        light: 'text-primary-700 border-0 border-none bg-neutral-100',
        dark: 'text-white border-0 border-none bg-neutral-900',
        accent: 'text-black border-0 border-none bg-accent-200',
        outline: 'bg-white border border-neutral-200',
        neutral: 'bg-neutral-50 border-none border-0',
        tag: 'bg-neutral-50 text-secondary-700 border-none border-0',
        surfaceAccent:
          'text-neutral-900 border-0 border-none bg-surface-badge-accent',
      },
      size: badgeSizeClass,
      'size-md': badgeSizeMdClass,
      'size-lg': badgeSizeLgClass,
      weight: badgeWeightClass,
      tone: {
        neutral: '',
        success: '',
        warning: '',
        error: '',
        brand: '',
        inverse: '',
        ghost: '',
      },
      emphasis: {
        subtle: '',
        strong: '',
      },
    },
    compoundVariants: [
      {
        tone: 'neutral',
        emphasis: 'subtle',
        class: 'bg-badge-neutral-subtle-bg text-badge-neutral-subtle-text',
      },
      {
        tone: 'neutral',
        emphasis: 'strong',
        class: 'bg-badge-neutral-strong-bg text-badge-neutral-strong-text',
      },
      {
        tone: 'success',
        emphasis: 'subtle',
        class: 'bg-badge-success-subtle-bg text-badge-success-subtle-text',
      },
      {
        tone: 'success',
        emphasis: 'strong',
        class: 'bg-badge-success-strong-bg text-badge-success-strong-text',
      },
      {
        tone: 'warning',
        emphasis: 'subtle',
        class: 'bg-badge-warning-subtle-bg text-badge-warning-subtle-text',
      },
      {
        tone: 'warning',
        emphasis: 'strong',
        class: 'bg-badge-warning-strong-bg text-badge-warning-strong-text',
      },
      {
        tone: 'error',
        emphasis: 'subtle',
        class: 'bg-badge-error-subtle-bg text-badge-error-subtle-text',
      },
      {
        tone: 'error',
        emphasis: 'strong',
        class: 'bg-badge-error-strong-bg text-badge-error-strong-text',
      },
      {
        tone: 'brand',
        emphasis: 'subtle',
        class: 'bg-badge-brand-subtle-bg text-badge-brand-subtle-text',
      },
      {
        tone: 'brand',
        emphasis: 'strong',
        class: 'bg-badge-brand-strong-bg text-badge-brand-strong-text',
      },
      {
        tone: 'inverse',
        emphasis: 'subtle',
        class: 'bg-badge-inverse-subtle-bg text-badge-inverse-subtle-text',
      },
      {
        tone: 'inverse',
        emphasis: 'strong',
        class: 'bg-badge-inverse-strong-bg text-badge-inverse-strong-text',
      },
      {
        tone: 'ghost',
        emphasis: 'subtle',
        class: 'bg-transparent text-badge-ghost-text px-0',
      },
      {
        tone: 'ghost',
        emphasis: 'strong',
        class: 'bg-transparent text-badge-ghost-text px-0',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'sm',
      emphasis: 'subtle',
    },
  },
);

/**
 * Type for Badge variant props
 */
export type BadgeVariantProps = VariantProps<typeof badgeVariants>;
